import { Container, Overlay } from "./SocketConnectionErrorHandler.style";
import {
  BigWifiOff,
  NoInternetConnectionTitle,
  SecondaryInfo,
  TertiaryInfo,
} from "./InternetConnectionErrorHandler.style";
import { InternetStatusContext, useTranslation } from "hhn-commons";
import React, { useContext, useEffect, useRef, useState } from "react";
import { userTracking } from "../analytics/userTracking";

interface InternetConnectionErrorHandlerProps {
  children: React.ReactNode;
}

export function InternetConnectionErrorHandler({
  children,
}: InternetConnectionErrorHandlerProps) {
  const { isConnected } = useContext(InternetStatusContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "networkInfo" });
  const previousIsConnected = useRef<boolean | undefined>(undefined);
  const [didDisconnect, setDidDisconnect] = useState(false);
  const offlineStartTime = useRef<number | null>(null);

  useEffect(() => {
    // User came back online after being offline
    if (didDisconnect && isConnected) {
      // Calculate offline duration
      if (offlineStartTime.current) {
        const offlineDuration = Math.floor(
          (Date.now() - offlineStartTime.current) / 1000,
        );
        userTracking.trackBackOnline(offlineDuration);
        offlineStartTime.current = null;
      }

      // Reload the page to restore connection
      window.location.reload();
    }

    // User just went offline
    if (previousIsConnected.current && !isConnected) {
      setDidDisconnect(true);
      offlineStartTime.current = Date.now();

      // Track that user saw offline screen
      userTracking.trackOfflineScreen();
    }

    previousIsConnected.current = isConnected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  return (
    <>
      {didDisconnect && (
        <Overlay>
          <Container>
            <BigWifiOff />
            <NoInternetConnectionTitle>
              {tNs("offline")}
            </NoInternetConnectionTitle>
            <SecondaryInfo>{tNs("offlineReason")}</SecondaryInfo>
            <TertiaryInfo>{tNs("willTryConnectingAgain")}</TertiaryInfo>
          </Container>
        </Overlay>
      )}
      {children}
    </>
  );
}
